import {
  Box,
  Button,
  Checkbox,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { Modal, TextField, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import { BORDER_COLOR, FONT_FAMILY, TEXT_COLOR } from "../../theme";
import { useContext, useState, ReactNode } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { addItemsModalStyles, addProjectModalStyles } from "./styles";
import { UserDetailsContext } from "../../contexts";
import { S3CredentialsContextInterface } from "../../contexts";
import { S3CredentialsContext } from "../../contexts";
import { AuthContext, AuthContextInterface } from "../../contexts";
import { CustomPaper } from "../../pages/MyTracks/styles";
import { SelectField, useStyles } from "../../pages/MyTracks/styles";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { Collaborator, CreateProjectResponse } from "../../proto/api_pb";
import { toast } from "react-toastify";
import { AddProjectModalProps } from "./types";
import { addProject } from "../../services/projectService";
import { awaitableUploadToS3 } from "../../utils/S3Utils";
import CustomButton from "../CustomButton/CustomButton";
import { MoodsArray } from "../constants";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const ASSETS_URL = process.env.REACT_APP_ASSET_S3_BUCKET_URL!;

export const AddProjectModal: React.FC<AddProjectModalProps> = ({
  enterpriseUsers,
  handleUseEffect,
  setProjects,
  projects,
  setPage
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [projectName, setProjectName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [isProjectNameEmptyError, setIsProjectNameEmptyError] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedCollaborators, setSelectedCollaborators] =
    useState<string[]>();
  const [file, setFile] = useState<File | null>(null);
  const [photo, setPhoto] = useState<string | null>(null);
  const { getValidAccessToken } = useGetValidAccessToken();
  const classes = useStyles();
  const isMobileScreen = useMediaQuery("(max-width:600px)");
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { awsAccessKeyId, awsSecretAccessKey, awsSessionToken } =
    useContext<S3CredentialsContextInterface>(S3CredentialsContext);
  const { enterprise, storageUsage } = useContext(UserDetailsContext);
  const [moods, setMoods] = useState<string[]>([]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setProjectName("");
    setDescription("");
    setSelectedCollaborators([]);
    setFile(null);
    setPhoto(null);
    setMoods([]);
    setIsLoading(false);
  };
  const filteredUserNames = enterpriseUsers
    .filter((user) => user.getUserId() !== userId)
    .map((user) => user.getUsername());

  const handleChangeCollaboratorsList = async (
    _event: React.SyntheticEvent<Element, Event>,
    value: unknown
  ) => {
    const values = value as Array<string>;
    const tempArr: string[] = [];
    const length = values.length;
    for (let i = 0; i < length; i++) {
      const tag_curr = values[i];
      tempArr.push(tag_curr);
    }
    setSelectedCollaborators(tempArr);
  };

  const handleAddProject = async () => {
    if (projectName.trim() === "") {
      setIsProjectNameEmptyError(true);
      toast.warn("Project name cannot be empty");
      return;
    }

    const isDuplicate = projects.some(
      (project) => project.getName().toLowerCase() === projectName.toLowerCase()
    );

    if (isDuplicate) {
      toast.warn("Project name already exists. Choose another.");
      return;
    }

    setIsLoading(true);
    const token = await getValidAccessToken();
    const collaborators: Collaborator[] = [];
    if (selectedCollaborators) {
      const selectedUsers = enterpriseUsers.filter((user) =>
        selectedCollaborators.includes(user.getUsername())
      );
      for (const user of selectedUsers) {
        const collaborator = new Collaborator();
        collaborator.setUserId(user.getUserId());
        collaborator.setUsername(user.getUsername());
        collaborator.setProfilePicture(user.getProfilePicture());
        collaborators.push(collaborator);
      }
    }
    let profilePictureString = "";
    if (file) {
      const safeFileName = file.name
        .normalize("NFKD")
        .replace(/[^\u0020-\u007E]/g, "");
      const key = await awaitableUploadToS3(
        userId,
        file,
        `project-art/${safeFileName}`,
        file.type,
        awsAccessKeyId!,
        awsSecretAccessKey!,
        awsSessionToken!,
        () => { }
      );
      profilePictureString = ASSETS_URL + "/" + key;
    }
    const res = await addProject(
      token,
      userId,
      projectName,
      description,
      collaborators,
      profilePictureString,
      enterprise?.id as string,
      moods
    );
    if (res instanceof CreateProjectResponse) {
      toast.info("Project created successfully");
      setProjects([]);
      setPage(0);
      await handleUseEffect();
      setIsLoading(false);
      handleClose();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const imageFile = Array.from(files).find((file) =>
        file.type.startsWith("image/")
      );
      if (imageFile) {
        const safeFileName = imageFile.name
          .normalize("NFKD")
          .replace(/[^\u0020-\u007E]/g, "");

        const sanitizedFile = new File([imageFile], safeFileName, {
          type: imageFile.type,
        });
        setFile(sanitizedFile);
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.result) {
            setPhoto(reader.result as string);
          }
        };
        reader.readAsDataURL(sanitizedFile);
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const imageFile = Array.from(files).find((file) =>
        file.type.startsWith("image/")
      );
      if (imageFile) {
        const safeFileName = imageFile.name
          .normalize("NFKD")
          .replace(/[^\u0020-\u007E]/g, "");

        const sanitizedFile = new File([imageFile], safeFileName, {
          type: imageFile.type,
        });
        setFile(sanitizedFile);
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.result) {
            setPhoto(reader.result as string);
          }
        };
        reader.readAsDataURL(sanitizedFile);
      }
    }
  };

  return (
    <>
      <Button
        sx={addProjectModalStyles.addProjectButton}
        onClick={() => {
          if (
            enterprise &&
            enterprise.storageLimit &&
            Math.min((storageUsage / enterprise?.storageLimit) * 100, 100) >=
            100
          ) {
            toast.warn(
              "Please upgrade your enterprise storage to add more projects"
            );
          } else {
            handleOpen();
          }
        }}
      >
        <CreateNewFolderOutlinedIcon />
        Create Project
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        style={{ maxHeight: "100vh", overflowY: "scroll" }}
      >
        <Box
          sx={{
            ...addProjectModalStyles.modal,
            ...{
              width: isMobileScreen ? "100%" : 400,
            },
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography
            sx={{ color: TEXT_COLOR, marginBottom: "20px" }}
            id="modal-modal-title"
            variant="h6"
            component="h2"
          >
            Create New Project
          </Typography>
          <Typography sx={{ color: TEXT_COLOR }}>
            Project Name
            <span className="asterisk">*</span>
          </Typography>
          <TextField
            error={isProjectNameEmptyError}
            onChange={(e) => {
              setIsProjectNameEmptyError(false);
              setProjectName(e.target.value);
            }}
            fullWidth
            placeholder="Enter Project Name"
          />
          <Typography sx={{ color: TEXT_COLOR, marginTop: "15px" }}>
            Project Description
          </Typography>
          <TextField
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            placeholder="Enter Project Description"
          />
          <Box sx={{ marginTop: "15px" }}>
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              sx={{
                ...addItemsModalStyles.uploadingBox,
                ...{
                  height: "200px",
                  border: `2px dashed ${BORDER_COLOR}`,
                },
              }}
            >
              <>
                <CloudUploadIcon sx={addItemsModalStyles.cloudUploadIcon} />
                <Typography variant="body1" sx={addItemsModalStyles.typography}>
                  Drag & Drop Artwork here or click the button below to upload.
                </Typography>
              </>
              <>
                <label htmlFor="file-upload">
                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    sx={addItemsModalStyles.chooseMoreFilesButton}
                  >
                    Choose Artwork for Project
                  </Button>
                </label>
              </>
            </Box>
            {photo && (
              <div>
                <img
                  src={photo}
                  alt="Uploaded"
                  style={{ width: "150px", height: "150px" }}
                />
              </div>
            )}
          </Box>
          <Typography sx={{ color: TEXT_COLOR, marginTop: "15px" }}>
            Project Mood
          </Typography>
          <SelectField
            value={moods}
            fullWidth
            multiple
            id="mood-select"
            options={MoodsArray}
            onChange={(_event, value) => setMoods(value as string[])}
            freeSolo
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select or type moods"
                InputLabelProps={{ shrink: false }}
                classes={{ root: classes.customTextField }}
              />
            )}
            PaperComponent={CustomPaper}
          />
          <Typography sx={{ color: TEXT_COLOR, marginTop: "15px" }}>
            Select Collaborators
          </Typography>
          <SelectField
            multiple
            fullWidth
            id="collaborator-tags"
            options={filteredUserNames}
            disableCloseOnSelect
            onChange={handleChangeCollaboratorsList}
            renderOption={(props, option, { selected }) => (
              <li {...props} style={{ fontFamily: FONT_FAMILY }}>
                <>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option as ReactNode}
                </>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={""}
                InputLabelProps={{ shrink: false }}
                classes={{ root: classes.customTextField }}
              />
            )}
            PaperComponent={CustomPaper}
          />
          <Box sx={addProjectModalStyles.bottomContainer}>
            <CustomButton
              text="Create"
              onClick={handleAddProject}
              loading={isLoading}
              customStyles={{ width: "48%" }}
            />
            <CustomButton
              text="Cancel"
              onClick={handleClose}
              isSkipButton={true}
              customStyles={{ width: "48%" }}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
};